/**
 * Admin SupportInboxPage
 *
 * Reads contact-form submissions and lets the admin resolve or delete them.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { AdminLayout } from '../layout';
import { useSupportMessages, SupportStatus } from './hooks/useSupportMessages';

const formatDate = (d: Date): string =>
  d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const TABS: { key: SupportStatus | 'all'; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All' },
];

export const SupportInboxPage: React.FC = () => {
  const [filter, setFilter] = useState<SupportStatus | 'all'>('open');
  const { messages, openCount, isLoading, setStatus, remove } = useSupportMessages(filter);

  const act = async (fn: Promise<void>, ok: string) => {
    try {
      await fn;
      toast.success(ok);
    } catch {
      toast.error('Action failed');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support Inbox</h1>
          <p className="text-sm text-gray-500 mt-1">
            Messages from the contact form. {openCount} open.
          </p>
        </div>

        <div className="flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === t.key
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t.label}
              {t.key === 'open' && openCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-white/20">
                  {openCount}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : messages.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No {filter} messages</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {messages.map((m) => (
                <li key={m.id} className={`p-4 ${m.status === 'resolved' ? 'bg-gray-50' : ''}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-900">{m.subject}</span>
                        {m.status === 'resolved' && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                            Resolved
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{m.message}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {m.name} ·{' '}
                        <a href={`mailto:${m.email}`} className="text-primary-600 hover:underline">
                          {m.email}
                        </a>{' '}
                        · {formatDate(m.createdAt)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {m.status === 'open' ? (
                        <button
                          onClick={() => act(setStatus(m.id, 'resolved'), 'Marked resolved')}
                          className="px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                        >
                          Resolve
                        </button>
                      ) : (
                        <button
                          onClick={() => act(setStatus(m.id, 'open'), 'Reopened')}
                          className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                          Reopen
                        </button>
                      )}
                      <button
                        onClick={() => act(remove(m.id), 'Deleted')}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default SupportInboxPage;
